import { useCallback, useEffect, useMemo, useState } from "react";
import {
  findMEmail,
  findMEmailAi,
  findMEmailAndGenerate,
} from "../../api/ModelsApi";
import {
  ActionIcon,
  Divider,
  Group,
  Loader,
  LoadingOverlay,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { replaceTokens } from "./previewUtils";
import { findSignature } from "../../api/ConfigsApi";
import { useLanguage } from "../../stores/LanguageStore";
import { IconExclamationMark, IconRotateClockwise } from "@tabler/icons-react";
import ModelEditor from "../Adder/ModelEditor";
import { useProfileStore } from "../../stores/UserStore";
import { useConfigStore } from "../../stores/ConfigStore";

const EmailPreviewer = ({
  model_id,
  subjectSize = "md",
  iframeHeight = 300,
  paperShadow = "sm",
  iframeStyle,
  html,
  subject,
  signatureId,
  disableSubject,
  contact,
  paperStyle,
  noSubjectTooltip,
  withEditor,
  otherTokens,
  prog_id,
  noTokenReplace,
  redVariables,
  onModelFound,
  needTime = true,
}) => {
  const lang = useLanguage((s) => s.language);
  const [modelProfile, setModelProfile] = useState({ html, subject });
  const [signProfile, setSignProfile] = useState(null);
  const [loading, setLoading] = useState(html ? false : true);
  const [loadingSignature, setLoadingSignature] = useState(false);
  const [loadingGen, setLoadingGen] = useState(false);

  const missingVariables = useMemo(() => {
    if (contact && modelProfile?.html) {
      const replacements =
        useProfileStore.getState().profile?.datafields_replacement;
      let tokens = useConfigStore.getState().datafields;
      let tokensInMessage = [
        ...new Set(
          [...modelProfile.html.matchAll(/%([\w\d_]+)%/g)].map(
            (match) => match[0]
          )
        ),
      ];

      let result = tokensInMessage.filter((messageToken) => {
        let token = tokens.find(
          (x) => x.identifier_placeholder === messageToken
        );

        if (token) {
          const contact_value = contact?.properties?.find(
            (x) => x.data_field_id === token.id || x.id === token.id
          )?.value;

          let replacement = replacements?.find(
            (x) => x.datafield_id === token.id
          )?.replacement;

          return !contact_value && !replacement;
        }
        return false;
      });

      return result.map((item) =>
        tokens.find((x) => x.identifier_placeholder === item)
      );
    }
    return [];
  }, [contact, modelProfile?.html]);

  const handleGen = useCallback(() => {
    setLoadingGen(true);
    setLoading(true);
    let func = prog_id ? findMEmailAi : findMEmailAndGenerate;
    func({
      id: model_id,
      contact_id: prog_id
        ? contact?.programmations.find((x) => x.programmation_id === prog_id)
            ?.contact_list_contact_id
        : contact?.id,
    })
      .then((res) => {
        setModelProfile(res.data.model_profile);
      })
      .finally(() => {
        setLoadingGen(false);
        setLoading(false);
      });
  }, [prog_id, model_id, contact?.programmations, contact?.id]);

  useEffect(() => {
    if (loading) {
      if (model_id) {
        findMEmail(model_id)
          .then((response) => {
            onModelFound?.(response.data.model_profile);
            setModelProfile(response.data.model_profile);
          })
          .finally(() => setLoading(false));
      } else if (!html) {
        setModelProfile(null);
        setLoading(false);
      }
    }
  }, [model_id, html, loading]);

  // useEffect(() => {
  //   if (!html) {
  //     setTimeout(() => {
  //       setLoading(true);
  //     }, needTime ? 600 : 200);
  //   }
  // }, [model_id, html]);

  useEffect(() => {
    if (signatureId) {
      setLoadingSignature(true);
      findSignature(signatureId)
        .then((response) =>
          setSignProfile(response.data.email_signature_profile)
        )
        .finally(() => setLoadingSignature(false));
    } else {
      setSignProfile(null);
    }
  }, [signatureId]);

  useEffect(() => {
    if (modelProfile?.is_ai && contact?.id) {
      handleGen();
    }
  }, [contact, handleGen, modelProfile?.is_ai]);

  return (
    <Stack key={modelProfile?.id} pos={"relative"} h={"100%"} flex={"1 1 auto"}>
      <LoadingOverlay visible={loading || loadingGen} />
      {loadingSignature && (
        <Loader size={18} pos={"absolute"} top={52} right={8} />
      )}
      {redVariables && contact && missingVariables.length > 0 && (
        <Group>
          <ThemeIcon color="red" variant="light" size={"lg"} radius={"md"}>
            <IconExclamationMark />
          </ThemeIcon>
          <Text fw={500} c={"red"}>
            {lang.model_emails.templates.error_text1}{" "}
            {missingVariables.map((item, i) => (
              <b
                key={item.id}
                style={{ textDecoration: "underline", marginRight: 12 }}
              >
                {item.name}
              </b>
            ))}{" "}
            {lang.model_emails.templates.error_text2}
          </Text>
        </Group>
      )}
      <Paper
        component={Stack}
        withBorder
        shadow={paperShadow}
        h={"100%"}
        flex={"1 1 auto"}
        gap={0}
        {...paperStyle}
      >
        {!disableSubject && (
          <>
            <Text size={subjectSize} ml={"xs"} mt={12} truncate>
              {lang.model_emails.create.dialog.subject} :{" "}
              {modelProfile?.is_ai && !modelProfile?.subject ? (
                "[ GENERATED SUBJECT ]"
              ) : (
                <b>
                  {replaceTokens({
                    otherTokens: otherTokens,
                    text: modelProfile?.subject,
                    contact,
                    shouldEncode: false,
                  }) || ""}
                </b>
              )}
            </Text>
            <Divider mt={12} />
          </>
        )}

        {modelProfile?.is_ai && (
          <>
            <Group pos={"relative"} gap={0} w={"100%"}>
              <Tooltip label={modelProfile?.prompt} disabled={noSubjectTooltip}>
                <Text
                  size={subjectSize}
                  ml={"xs"}
                  mt={6}
                  flex={"1 1 auto"}
                  truncate
                  mr={16}
                  w={0}
                >
                  Prompt : <b>{modelProfile?.prompt}</b>
                </Text>
              </Tooltip>

              {withEditor && (
                <ModelEditor
                  model={modelProfile}
                  onModelEdited={({ closer }) => {
                    closer?.();
                    setLoading(true);
                    handleGen?.();
                  }}
                  type={"email"}
                  variant="light"
                  radius={0}
                />
              )}
              {contact && (
                <Tooltip label="Regénérer le modèle">
                  <ActionIcon
                    top={0}
                    right={0}
                    radius={0}
                    size={"lg"}
                    h={"100%"}
                    loading={loadingGen}
                    onClick={handleGen}
                  >
                    <IconRotateClockwise size={18} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
            <Divider />
          </>
        )}

        <Stack
          style={{ overflow: "auto" }}
          h={disableSubject ? "100%" : "calc(100% - 35px)"}
          flex={1}
        >
          <iframe
            key={modelProfile?.id}
            title={modelProfile?.subject}
            srcDoc={`<div style="font-family: sans-serif;">
    ${(
      replaceTokens({
        otherTokens,
        text:
          modelProfile?.is_ai && !contact
            ? "[ GENERATED CONTENT ]"
            : modelProfile?.html,
        signHtml: signProfile?.html,
        contact,
        noTokenReplace,
        redVariables,
      }) || ""
    )
      // force links to open in new tab
      .replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ')}
  </div>`}
            style={{
              height: iframeHeight,
              width: "100%",
              border: "none",
              ...iframeStyle,
            }}
            sandbox="allow-popups allow-popups-to-escape-sandbox"
          />
        </Stack>
      </Paper>
    </Stack>
  );
};

export default EmailPreviewer;
